import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, TextField, IconButton, Button, Switch, Alert, FormControl, Select, MenuItem, Checkbox, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { v4 as uuidv4 } from 'uuid';
import { adminAgentsAPI, canvasVersionsAPI } from '../../../services/api';
import PromptEditor from './PromptEditor';
import EdgeProperties from './EdgeProperties';
import { toSnakeCase, FORMS_TEMPLATE_MARKER } from './promptUtils';
import { PROMPT_LIKE_TYPES } from './traversePromptGraph';

function PromptNodeProperties({ node, onUpdateNode, agentName, addToolNodeRef, addSubAgentNodeRef, toolMethods, subAgents, schemaFields }) {
  const handleContentChange = useCallback((text) => {
    onUpdateNode(node.id, { content: text });
  }, [node.id, onUpdateNode]);

  const handleLabelChange = useCallback((e) => {
    onUpdateNode(node.id, { label: e.target.value });
  }, [node.id, onUpdateNode]);

  const handleAddToolNode = addToolNodeRef?.current
    ? (method) => addToolNodeRef.current(method, node.id)
    : null;
  const handleAddSubAgentNode = addSubAgentNodeRef?.current
    ? (subAgent) => addSubAgentNodeRef.current(subAgent, node.id)
    : null;

  const tools = node.data?.tools || [];

  const handleBindingChange = (toolClass, methodName, key, value) => {
    const updated = tools.map((t) =>
      t.toolClass === toolClass && t.methodName === methodName
        ? { ...t, parameterBindings: { ...t.parameterBindings, [key]: value } }
        : t
    );
    onUpdateNode(node.id, { tools: updated });
  };
  const handleAddBinding = (toolClass, methodName, key) => handleBindingChange(toolClass, methodName, key, '');
  const handleRemoveBinding = (toolClass, methodName, key) => {
    const updated = tools.map((t) => {
      if (t.toolClass !== toolClass || t.methodName !== methodName) return t;
      const b = { ...t.parameterBindings };
      delete b[key];
      return { ...t, parameterBindings: b };
    });
    onUpdateNode(node.id, { tools: updated });
  };

  const isTemplateManagedForms =
    node.type === 'forms' && (node.data?.content || '').trim() === FORMS_TEMPLATE_MARKER;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        label="Name"
        size="small"
        value={node.data.label || ''}
        onChange={handleLabelChange}
        inputProps={{ style: { fontSize: 11 } }}
      />
      {isTemplateManagedForms && (
        <Alert severity="info" sx={{ fontSize: 10, py: 0.5 }}>
          Managed centrally — this node tracks the shared FORMS template
          (agentflow/prompt_templates/forms.txt), resolved when each ticket&apos;s
          prompt is assembled. Replace the {FORMS_TEMPLATE_MARKER} token with your
          own text to customise the protocol for this agent only.
        </Alert>
      )}
      <Accordion disableGutters defaultExpanded sx={{ bgcolor: 'transparent', boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1, '&::before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />} sx={{ minHeight: 32, px: 1, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Prompt</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1, pt: 0 }}>
          <PromptEditor
            agentName={agentName}
            content={node.data.content || ''}
            onContentChange={handleContentChange}
            toolMethods={toolMethods}
            subAgents={subAgents}
            schemaFields={schemaFields}
            onAddToolNode={handleAddToolNode}
            onAddSubAgentNode={handleAddSubAgentNode}
          />
        </AccordionDetails>
      </Accordion>
      {tools.length > 0 && (
        <Accordion disableGutters defaultExpanded={false} sx={{ bgcolor: 'transparent', boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1, '&::before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />} sx={{ minHeight: 32, px: 1, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography sx={{ fontSize: 10, color: '#22c55e', fontFamily: 'monospace' }}>$ Tool Calls ({tools.length})</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tools.map((t) => (
              <MethodBindingEditor
                key={`${t.toolClass}.${t.methodName}`}
                method={{ name: `${toSnakeCase(t.toolClass)}.${t.methodName}`, parameterBindings: t.parameterBindings || {} }}
                onBindingChange={(_, key, value) => handleBindingChange(t.toolClass, t.methodName, key, value)}
                onAddBinding={(_, key) => handleAddBinding(t.toolClass, t.methodName, key)}
                onRemoveBinding={(_, key) => handleRemoveBinding(t.toolClass, t.methodName, key)}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

function SubAgentBindingEditor({ subAgent, onBindingChange, onAddBinding, onRemoveBinding, onVersionPinChange, onOpenCanvas }) {
  const [newKey, setNewKey] = useState('');
  const bindings = subAgent.inputBindings || {};

  const [versions, setVersions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    canvasVersionsAPI
      .list(subAgent.agentName)
      .then((d) => { if (!cancelled) setVersions(d.versions || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [subAgent.agentName]);

  const handleAdd = () => {
    if (!newKey.trim()) return;
    onAddBinding(subAgent.agentName, newKey.trim());
    setNewKey('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 10, color: '#7ae0a0' }}>@{subAgent.agentName}</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select
            value={subAgent.versionPin || 'latest'}
            onChange={(e) => onVersionPinChange?.(subAgent.agentName, e.target.value)}
            sx={{ fontSize: 9, '& .MuiSelect-select': { py: 0.25, px: 0.75 } }}
          >
            <MenuItem value="latest" sx={{ fontSize: 10 }}>latest</MenuItem>
            {versions.map((v) => (
              <MenuItem key={v.id} value={v.label} sx={{ fontSize: 10 }}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="text" color="success" sx={{ fontSize: 9, py: 0, minWidth: 0 }} onClick={() => onOpenCanvas?.(subAgent.agentName)}>
          Open →
        </Button>
      </Box>
      <Typography variant="overline" sx={{ fontSize: 9 }}>Input Data</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>
        {'Data passed to the sub-agent. Available as {{input.key}} inside it.'}
      </Typography>
      {Object.entries(bindings).map(([key, value]) => (
        <Box key={key} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField size="small" value={key} disabled inputProps={{ style: { fontSize: 10 } }} sx={{ width: 90 }} />
          <TextField
            size="small"
            value={value}
            onChange={(e) => onBindingChange(subAgent.agentName, key, e.target.value)}
            inputProps={{ style: { fontSize: 10, fontFamily: 'monospace' } }}
            sx={{ flex: 1 }}
          />
          <IconButton size="small" onClick={() => onRemoveBinding(subAgent.agentName, key)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TextField
          size="small"
          placeholder="key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          inputProps={{ style: { fontSize: 10 } }}
          sx={{ flex: 1 }}
        />
        <Button size="small" variant="outlined" onClick={handleAdd} disabled={!newKey.trim()}>+ Add</Button>
      </Box>
    </Box>
  );
}

function SubAgentContainerNodeProperties({ node, onUpdateNode, onOpenCanvas }) {
  const { subAgents = [] } = node.data;

  const handleBindingChange = (agentName, key, value) => {
    const updated = subAgents.map((s) =>
      s.agentName === agentName ? { ...s, inputBindings: { ...s.inputBindings, [key]: value } } : s
    );
    onUpdateNode(node.id, { subAgents: updated });
  };

  const handleAddBinding = (agentName, key) => {
    handleBindingChange(agentName, key, '');
  };

  const handleRemoveBinding = (agentName, key) => {
    const updated = subAgents.map((s) => {
      if (s.agentName !== agentName) return s;
      const bindings = { ...s.inputBindings };
      delete bindings[key];
      return { ...s, inputBindings: bindings };
    });
    onUpdateNode(node.id, { subAgents: updated });
  };

  const handleVersionPinChange = (agentName, versionPin) => {
    const updated = subAgents.map((s) =>
      s.agentName === agentName ? { ...s, versionPin } : s
    );
    onUpdateNode(node.id, { subAgents: updated });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#7ae0a0', fontWeight: 'bold', fontSize: 14 }}>@</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>Sub-Agents</Typography>
      </Box>
      {subAgents.map((s) => (
        <SubAgentBindingEditor
          key={s.agentName}
          subAgent={s}
          onBindingChange={handleBindingChange}
          onAddBinding={handleAddBinding}
          onRemoveBinding={handleRemoveBinding}
          onVersionPinChange={handleVersionPinChange}
          onOpenCanvas={onOpenCanvas}
        />
      ))}
    </Box>
  );
}

function MethodBindingEditor({ method, onBindingChange, onAddBinding, onRemoveBinding }) {
  const [newKey, setNewKey] = useState('');
  const bindings = method.parameterBindings || {};

  const handleAdd = () => {
    if (!newKey.trim()) return;
    onAddBinding(method.name, newKey.trim());
    setNewKey('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 10, color: '#e0b07a' }}>.{method.name}()</Typography>
      <Typography variant="overline" sx={{ fontSize: 9 }}>Parameter Bindings</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>
        {'Use {{ticket.data.field}} for template values or enter a static value.'}
      </Typography>
      {Object.entries(bindings).map(([key, value]) => (
        <Box key={key} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField size="small" value={key} disabled inputProps={{ style: { fontSize: 10 } }} sx={{ width: 90 }} />
          <TextField
            size="small"
            value={value}
            onChange={(e) => onBindingChange(method.name, key, e.target.value)}
            inputProps={{ style: { fontSize: 10, fontFamily: 'monospace' } }}
            sx={{ flex: 1 }}
          />
          <IconButton size="small" onClick={() => onRemoveBinding(method.name, key)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TextField
          size="small"
          placeholder="param name"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          inputProps={{ style: { fontSize: 10 } }}
          sx={{ flex: 1 }}
        />
        <Button size="small" variant="outlined" onClick={handleAdd} disabled={!newKey.trim()}>+ Add</Button>
      </Box>
    </Box>
  );
}

function ToolContainerNodeProperties({ node, onUpdateNode }) {
  const { tools = [] } = node.data;

  const handleBindingChange = (toolClass, methodName, key, value) => {
    const updated = tools.map((t) =>
      t.toolClass === toolClass && t.methodName === methodName
        ? { ...t, parameterBindings: { ...t.parameterBindings, [key]: value } }
        : t
    );
    onUpdateNode(node.id, { tools: updated });
  };

  const handleAddBinding = (toolClass, methodName, key) => {
    handleBindingChange(toolClass, methodName, key, '');
  };

  const handleRemoveBinding = (toolClass, methodName, key) => {
    const updated = tools.map((t) => {
      if (t.toolClass !== toolClass || t.methodName !== methodName) return t;
      const bindings = { ...t.parameterBindings };
      delete bindings[key];
      return { ...t, parameterBindings: bindings };
    });
    onUpdateNode(node.id, { tools: updated });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#e0b07a', fontWeight: 'bold', fontSize: 14 }}>$</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>Tools</Typography>
      </Box>
      {tools.map((t) => (
        <MethodBindingEditor
          key={`${t.toolClass}.${t.methodName}`}
          method={{ name: `${toSnakeCase(t.toolClass)}.${t.methodName}`, parameterBindings: t.parameterBindings || {} }}
          onBindingChange={(_, key, value) => handleBindingChange(t.toolClass, t.methodName, key, value)}
          onAddBinding={(_, key) => handleAddBinding(t.toolClass, t.methodName, key)}
          onRemoveBinding={(_, key) => handleRemoveBinding(t.toolClass, t.methodName, key)}
        />
      ))}
    </Box>
  );
}

const COLOR_GLOBAL_TOOLS = '#0891b2';

function GlobalToolsNodeProperties({ node, onUpdateNode, toolMethods }) {
  const tools = node.data?.tools || [];
  const [search, setSearch] = useState('');

  const addTool = (method) => {
    const exists = tools.some((t) => t.toolClass === method.toolClass && t.methodName === method.name);
    if (exists) return;
    onUpdateNode(node.id, {
      tools: [...tools, { toolClass: method.toolClass, methodName: method.name, moduleId: method.moduleId, parameterBindings: {} }],
    });
  };

  const removeTool = (toolClass, methodName) => {
    onUpdateNode(node.id, { tools: tools.filter((t) => !(t.toolClass === toolClass && t.methodName === methodName)) });
  };

  const filtered = toolMethods.filter((m) => {
    const q = search.toLowerCase();
    return !q || `${toSnakeCase(m.toolClass)}.${m.name}`.toLowerCase().includes(q);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: COLOR_GLOBAL_TOOLS, fontWeight: 'bold', fontSize: 14 }}>⊕</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>Available Tools</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>
        Tools the agent can use that are not explicitly called in the prompt.
      </Typography>

      {tools.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="overline" sx={{ fontSize: 9 }}>Added</Typography>
          {tools.map((t) => (
            <Box key={`${t.toolClass}.${t.methodName}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 10, color: COLOR_GLOBAL_TOOLS, flex: 1 }}>
                ${toSnakeCase(t.toolClass)}.{t.methodName}()
              </Typography>
              <IconButton size="small" onClick={() => removeTool(t.toolClass, t.methodName)}><CloseIcon sx={{ fontSize: 12 }} /></IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="overline" sx={{ fontSize: 9 }}>Add Tools</Typography>
      <TextField
        size="small"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        inputProps={{ style: { fontSize: 10 } }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 240, overflowY: 'auto' }}>
        {filtered.map((m) => {
          const alreadyAdded = tools.some((t) => t.toolClass === m.toolClass && t.methodName === m.name);
          return (
            <Box
              key={`${m.toolClass}.${m.name}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: alreadyAdded ? 'success.dark' : 'divider', borderRadius: 1, px: 1, py: 0.5, opacity: alreadyAdded ? 0.5 : 1 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 10, color: alreadyAdded ? 'success.main' : 'text.primary' }}>
                  ${toSnakeCase(m.toolClass)}.{m.name}()
                </Typography>
                {m.description && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9 }}>{m.description}</Typography>
                )}
              </Box>
              <Button size="small" variant="text" disabled={alreadyAdded} onClick={() => addTool(m)} sx={{ fontSize: 9, minWidth: 0, py: 0 }}>
                {alreadyAdded ? '✓' : '+ Add'}
              </Button>
            </Box>
          );
        })}
        {filtered.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9 }}>No tools match</Typography>
        )}
      </Box>
    </Box>
  );
}

function SettingsNodeProperties({ agentName }) {
  const [form, setForm] = useState({
    description: '',
    model_name: '',
    simple_model_name: '',
    allow_multi_user: false,
    accepts_file_uploads: false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    adminAgentsAPI.listAgents().then((agents) => {
      const agent = (agents || []).find((a) => a.name === agentName);
      if (agent && mountedRef.current) {
        setForm({
          description: agent.description || '',
          model_name: agent.model_name || '',
          simple_model_name: agent.simple_model_name || '',
          allow_multi_user: agent.allow_multi_user ?? false,
          accepts_file_uploads: agent.accepts_file_uploads ?? false,
        });
      }
    });
    return () => { mountedRef.current = false; };
  }, [agentName]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminAgentsAPI.updateAgent(agentName, form);
      if (mountedRef.current) {
        setSaved(true);
        setTimeout(() => { if (mountedRef.current) setSaved(false); }, 2000);
      }
    } catch {
      if (mountedRef.current) setError('Failed to save settings');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#b07ae0', fontSize: 14 }}>⚙</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>Settings</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ fontSize: 10 }}>{error}</Alert>}
      <TextField label="Description" size="small" multiline minRows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} inputProps={{ style: { fontSize: 11 } }} />
      <TextField label="Model" size="small" value={form.model_name} onChange={(e) => setForm((f) => ({ ...f, model_name: e.target.value }))} inputProps={{ style: { fontSize: 11 } }} />
      <TextField label="Simple Model" size="small" value={form.simple_model_name} onChange={(e) => setForm((f) => ({ ...f, simple_model_name: e.target.value }))} inputProps={{ style: { fontSize: 11 } }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 11 }}>Allow multi-user</Typography>
        <Switch checked={form.allow_multi_user} onChange={(e) => setForm((f) => ({ ...f, allow_multi_user: e.target.checked }))} size="small" />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 11 }}>Accept file uploads</Typography>
        <Switch checked={form.accepts_file_uploads} onChange={(e) => setForm((f) => ({ ...f, accepts_file_uploads: e.target.checked }))} size="small" />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9, mt: -1 }}>
        Applies to canvas-built agents. Agents backed by a Python class set this in code and ignore this toggle.
      </Typography>
      <Button size="small" variant="contained" onClick={handleSave} disabled={saving} color={saved ? 'success' : 'primary'}>
        {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save'}
      </Button>
    </Box>
  );
}

const FIELD_TYPES = ['string', 'integer', 'float', 'boolean', 'datetime', 'email', 'url', 'file_upload', 'select', 'multi_select'];

function toChoiceValue(label) {
  return label.trim().toLowerCase().replace(/\s+/g, '_');
}

function OptionsEditor({ choices, onChange }) {
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');

  const commit = () => {
    const l = label.trim();
    const v = value.trim() || toChoiceValue(l);
    if (l && !choices.some((c) => c.value === v)) {
      onChange([...choices, { value: v, label: l }]);
    }
    setLabel('');
    setValue('');
  };

  return (
    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>Options</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {choices.map((c) => (
          <Chip
            key={c.value}
            label={c.label !== c.value ? `${c.label} (${c.value})` : c.label}
            size="small"
            onDelete={() => onChange(choices.filter((o) => o.value !== c.value))}
            sx={{ fontSize: 9, height: 20 }}
          />
        ))}
        {choices.length === 0 && (
          <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', fontStyle: 'italic' }}>No options yet</Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TextField
          size="small"
          placeholder="Label"
          value={label}
          onChange={(e) => { setLabel(e.target.value); setValue(toChoiceValue(e.target.value)); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          inputProps={{ style: { fontSize: 10, padding: '3px 6px' } }}
          sx={{ flex: 2 }}
        />
        <TextField
          size="small"
          placeholder="value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          inputProps={{ style: { fontSize: 10, padding: '3px 6px' } }}
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={commit}
          disabled={!label.trim()}
          sx={{ fontSize: 10, minWidth: 44, py: 0.25 }}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
}

function DataSchemaNodeProperties({ node, agentName, onUpdateNode }) {
  const [fields, setFields] = useState([]);
  const [error, setError] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const nodeFields = node?.data?.fields;
    if (nodeFields?.length) {
      setFields(nodeFields.map((f) => ({ _id: uuidv4(), name: f.name, type: f.type || 'string', mandatory: f.mandatory || false, choices: f.choices || undefined })));
    } else {
      adminAgentsAPI.listAgents().then((agents) => {
        const agent = (agents || []).find((a) => a.name === agentName);
        if (agent && mountedRef.current) {
          const schema = agent.initial_data_schema || [];
          setFields(schema.map((f) => ({ _id: uuidv4(), name: f.name, type: f.type || 'string', mandatory: f.mandatory || false, choices: f.choices || undefined })));
        }
      });
    }
    return () => { mountedRef.current = false; };
  }, [agentName, node?.id]);

  const syncFields = (next) => {
    setFields(next);
    const cleanFields = next.map(({ _id, ...rest }) => rest);
    onUpdateNode?.(node.id, { fields: cleanFields });
  };

  const addField = () => syncFields([...fields, { _id: uuidv4(), name: '', type: 'string', mandatory: false }]);
  const removeField = (i) => syncFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, patch) => syncFields(fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#7ae0a0', fontSize: 14 }}>⊞</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>Data Schema</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ fontSize: 10 }}>{error}</Alert>}
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>
        Fields collected when a ticket is created.
      </Typography>
      {fields.map((f, i) => (
        <Box key={f._id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <TextField
              size="small" placeholder="name" value={f.name}
              onChange={(e) => updateField(i, { name: e.target.value })}
              inputProps={{ style: { fontSize: 10 } }}
              sx={{ flex: 1 }}
            />
            <Select
              size="small" value={f.type}
              onChange={(e) => updateField(i, { type: e.target.value, choices: e.target.value === 'select' || e.target.value === 'multi_select' ? (f.choices || []) : undefined })}
              sx={{ fontSize: 10, minWidth: 80 }}
            >
              {FIELD_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 10 }}>{t}</MenuItem>)}
            </Select>
            <Checkbox
              size="small" checked={f.mandatory}
              onChange={(e) => updateField(i, { mandatory: e.target.checked })}
              title="Mandatory"
            />
            <IconButton size="small" onClick={() => removeField(i)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          {(f.type === 'select' || f.type === 'multi_select') && (
            <OptionsEditor
              choices={f.choices || []}
              onChange={(opts) => updateField(i, { choices: opts })}
            />
          )}
        </Box>
      ))}
      <Button size="small" variant="outlined" onClick={addField}>+ Add field</Button>
    </Box>
  );
}

const SLOT_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'exists', label: 'exists' },
];

function VariantConditionPicker({ condition, onChange }) {
  const field = condition?.field || '';
  const operator = condition?.operator || 'equals';
  const value = condition?.value || '';

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      <TextField
        size="small"
        placeholder="field (e.g. region)"
        value={field}
        onChange={(e) => onChange({ field: e.target.value, operator, value })}
        sx={{ flex: 2, minWidth: 80, '& .MuiInputBase-input': { fontSize: 11 } }}
      />
      <Select
        size="small"
        value={operator}
        onChange={(e) => {
          const op = e.target.value;
          onChange({ field, operator: op, value: op === 'exists' ? '' : value });
        }}
        sx={{ fontSize: 11, minWidth: 80 }}
      >
        {SLOT_OPERATORS.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 11 }}>{o.label}</MenuItem>
        ))}
      </Select>
      {operator !== 'exists' && (
        <TextField
          size="small"
          placeholder="value"
          value={value}
          onChange={(e) => onChange({ field, operator, value: e.target.value })}
          sx={{ flex: 2, minWidth: 60, '& .MuiInputBase-input': { fontSize: 11 } }}
        />
      )}
    </Box>
  );
}

function PromptSlotNodeProperties({ node, onUpdateNode }) {
  const variants = node.data.variants || [];
  const fallback = node.data.fallback ?? '';

  const update = (newVariants, newFallback) => {
    onUpdateNode(node.id, { variants: newVariants, fallback: newFallback });
  };

  const addVariant = () => {
    update([...variants, { id: uuidv4(), condition: { field: '', operator: 'equals', value: '' }, content: '' }], fallback);
  };

  const removeVariant = (id) => {
    update(variants.filter((v) => v.id !== id), fallback);
  };

  const moveVariant = (id, direction) => {
    const idx = variants.findIndex((v) => v.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= variants.length) return;
    const updated = [...variants];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    update(updated, fallback);
  };

  const updateVariant = (id, patch) => {
    update(variants.map((v) => v.id === id ? { ...v, ...patch } : v), fallback);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 1, py: 1 }}>
      <Typography variant="overline" sx={{ fontSize: 9 }}>
        Prompt Slot
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#0891b2', fontSize: 12 }}
      >
        {`{{ ${node.data.name || 'unnamed'} }}`}
      </Typography>

      <Typography variant="overline" sx={{ fontSize: 9, mt: 0.5 }}>Variants (first match wins)</Typography>

      {variants.map((v, idx) => (
        <Box
          key={v.id}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1 }}>
              Variant {idx + 1}
            </Typography>
            <IconButton size="small" onClick={() => moveVariant(v.id, -1)} disabled={idx === 0} sx={{ p: 0.25 }}>
              <Typography sx={{ fontSize: 10 }}>▲</Typography>
            </IconButton>
            <IconButton size="small" onClick={() => moveVariant(v.id, 1)} disabled={idx === variants.length - 1} sx={{ p: 0.25 }}>
              <Typography sx={{ fontSize: 10 }}>▼</Typography>
            </IconButton>
            <IconButton size="small" onClick={() => removeVariant(v.id)} sx={{ p: 0.25, color: 'error.main' }}>
              <Typography sx={{ fontSize: 10 }}>✕</Typography>
            </IconButton>
          </Box>
          <VariantConditionPicker
            condition={v.condition}
            onChange={(cond) => updateVariant(v.id, { condition: cond })}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            placeholder="Content when this condition matches..."
            value={v.content}
            onChange={(e) => updateVariant(v.id, { content: e.target.value })}
            sx={{ '& .MuiInputBase-input': { fontSize: 11 } }}
          />
        </Box>
      ))}

      <Button
        size="small"
        variant="outlined"
        onClick={addVariant}
        sx={{ fontSize: 11, textTransform: 'none' }}
      >
        + Add variant
      </Button>

      <Box sx={{ mt: 0.5 }}>
        <Typography variant="overline" sx={{ fontSize: 9 }}>Default (no match)</Typography>
        <TextField
          size="small"
          multiline
          minRows={2}
          fullWidth
          placeholder="Leave blank to substitute empty string..."
          value={fallback}
          onChange={(e) => update(variants, e.target.value)}
          sx={{ mt: 0.5, '& .MuiInputBase-input': { fontSize: 11 } }}
        />
      </Box>
    </Box>
  );
}

function isRootPrompt(node, canvasEdges) {
  if (node.type !== 'prompt') return false;
  return !canvasEdges.some((e) => e.target === node.id);
}

export default function NodeProperties({ selectedElement, onUpdateNode, onUpdateEdge, onDeleteNode, onDuplicateNode, agentName, addToolNodeRef, addSubAgentNodeRef, onOpenCanvas, width = 260, onCollapse, canvasNodes = [], canvasEdges = [] }) {
  const schemaFields = useMemo(() => {
    const node = canvasNodes.find((n) => n.type === 'data_schema');
    return node?.data?.fields || [];
  }, [canvasNodes]);
  const [toolMethods, setToolMethods] = useState([]);
  const [subAgents, setSubAgents] = useState([]);

  useEffect(() => {
    adminAgentsAPI.listAvailableTools().then((tools) => {
      setToolMethods((tools || []).flatMap((t) =>
        (t.functions || []).map((fn) => ({
          name: fn.name,
          toolClass: t.class_name,
          moduleId: t.id,
          description: fn.description,
          parameters: fn.parameters || [],
        }))
      ));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!agentName) return;
    let cancelled = false;
    adminAgentsAPI
      .listAgents()
      .then(async (agents) => {
        const subs = (agents || []).filter((a) => a.parent_agent === agentName);
        const enriched = await Promise.all(
          subs.map(async (sub) => {
            try {
              const v = await canvasVersionsAPI.list(sub.name);
              return { ...sub, versions: (v.versions || []).map((row) => row.label).filter(Boolean) };
            } catch {
              return { ...sub, versions: [] };
            }
          })
        );
        if (!cancelled) setSubAgents(enriched);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [agentName]);

  const isEdge = selectedElement && 'source' in selectedElement && 'target' in selectedElement;
  const selectedNode = isEdge ? null : selectedElement;

  const isStructural = selectedNode?.type === 'settings';
  const deletable = selectedNode && !isStructural && !isRootPrompt(selectedNode, canvasEdges);
  const duplicable = selectedNode && !isStructural;

  return (
    <Box sx={{ width, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ fontSize: 9, letterSpacing: 1 }}>Properties</Typography>
        {onCollapse && (
          <IconButton size="small" onClick={onCollapse} title="Hide properties">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto' }}>
        {!selectedElement && (
          <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', mt: 4 }}>
            Select a node or edge
          </Typography>
        )}
        {isEdge && (
          <EdgeProperties key={selectedElement.id} edge={selectedElement} onUpdateEdge={onUpdateEdge} />
        )}
        {PROMPT_LIKE_TYPES.has(selectedNode?.type) && (
          <PromptNodeProperties key={selectedNode.id} node={selectedNode} onUpdateNode={onUpdateNode} agentName={agentName} addToolNodeRef={addToolNodeRef} addSubAgentNodeRef={addSubAgentNodeRef} toolMethods={toolMethods} subAgents={subAgents} schemaFields={schemaFields} />
        )}
        {selectedNode?.type === 'sub_agent_container' && (
          <SubAgentContainerNodeProperties key={selectedNode.id} node={selectedNode} onUpdateNode={onUpdateNode} onOpenCanvas={onOpenCanvas} />
        )}
        {selectedNode?.type === 'settings' && (
          <SettingsNodeProperties key={selectedNode.id} agentName={agentName} />
        )}
        {selectedNode?.type === 'data_schema' && (
          <DataSchemaNodeProperties key={selectedNode.id} node={selectedNode} agentName={agentName} onUpdateNode={onUpdateNode} />
        )}
        {selectedNode?.type === 'global_tools' && (
          <GlobalToolsNodeProperties key={selectedNode.id} node={selectedNode} onUpdateNode={onUpdateNode} toolMethods={toolMethods} />
        )}
        {selectedNode?.type === 'prompt_slot' && (
          <PromptSlotNodeProperties key={selectedNode.id} node={selectedNode} onUpdateNode={onUpdateNode} />
        )}
      </Box>
      {(deletable || duplicable) && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1, display: 'flex', gap: 1 }}>
          {duplicable && (
            <Button
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
              onClick={() => onDuplicateNode?.(selectedNode)}
              variant="outlined"
              sx={{ flex: 1, fontSize: 10, py: 0.5 }}
            >
              Duplicate
            </Button>
          )}
          {deletable && (
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
              color="error"
              onClick={() => onDeleteNode?.(selectedNode.id)}
              variant="outlined"
              sx={{ flex: 1, fontSize: 10, py: 0.5 }}
            >
              Delete
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
